import { useCallback, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { Banner, BannerVariants, Button, Form, Icon, Text } from '@/ui'

import { makeReqEmailSchema, makeReqStringSchema } from '@/base'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import {
  INVITE_KNOWN_RECIPIENT_KEY,
  inviteKnownRecipient,
  KnownRecipientResponse,
} from './slice'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import { useAnalyticsTrack } from '@/features/amplitude'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

const createErroneousMessage = (recipients: KnownRecipientResponse[]) => {
  const alreadyExistingUsers = recipients.filter(
    (r) => r.errorCode === 'rf_user_already_exists'
  )
  const errors = recipients.filter(
    (r) =>
      r.errorCode === 'heard_db_error' ||
      r.errorCode === 'sendgrid_error' ||
      r.errorCode === 'rf_user_creation_error'
  )

  const verb = alreadyExistingUsers.length > 1 ? 'have' : 'has'

  let details =
    alreadyExistingUsers.length > 0
      ? `${alreadyExistingUsers
          .map((u) => u.email)
          .join(', ')} ${verb} already been invited.`
      : ''

  if (errors.length > 0) {
    details += '\nThere was an error processing this request. Please try again'
  }

  return details
}

const ReferralEmailForm = () => {
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const dispatch = useAppDispatch()

  const loading = useReselector(getIsFetching, INVITE_KNOWN_RECIPIENT_KEY)
  const error = useReselector(getFetchError, INVITE_KNOWN_RECIPIENT_KEY)
  const track = useAnalyticsTrack()
  const [postResponse, setPostResponse] = useState<{
    successful: KnownRecipientResponse[]
    erroneous: KnownRecipientResponse[]
  }>()

  const formik = useFormik({
    initialValues: {
      referrals: [{ firstName: '', lastName: '', email: '' }],
    },
    validateOnMount: true,
    onSubmit: async (values) => {
      const { referrals } = values
      const res = await inviteKnownRecipient(referrals)(dispatch)

      if (res) {
        setPostResponse(res)
        track('clicked referral send email button')
        // Remove successful results from form
        for (const s of res.successful) {
          const index = referrals.findIndex((r) => r.email === s.email)
          handleRemove(index)
        }
      }
    },
  })

  const { isValid, submitForm, values, setFieldValue, validateForm } = formik

  const handleRemove = useCallback(
    (idx: number) => {
      const newReferrals = [...values.referrals]
      newReferrals.splice(idx, 1)
      setFieldValue('referrals', newReferrals).then(() => {
        // This should be automatic but doesn't seem to work for some reason
        validateForm()
      })
    },
    [setFieldValue, values.referrals, validateForm]
  )

  return (
    <div className="flex flex-col gap-4 pt-4">
      <Text as={TypographyStyles.H3}>Invite by email</Text>
      <FormikProvider value={formik}>
        <div className="flex flex-col gap-4">
          {values.referrals.map((_, index) => (
            <div
              key={`referral-${index}`}
              className="flex gap-4 w-full items-start"
            >
              <Form.Input
                wrapperClassName="flex-1"
                disabled={loading}
                name={`referrals[${index}].firstName`}
                required
                placeholder="Friend's first name"
                schema={makeReqStringSchema()}
              />
              <Form.Input
                wrapperClassName="flex-1"
                disabled={loading}
                name={`referrals[${index}].lastName`}
                required
                placeholder="Friend's last name"
                schema={makeReqStringSchema()}
              />
              <Form.Input
                wrapperClassName="flex-1"
                disabled={loading}
                name={`referrals[${index}].email`}
                required
                placeholder="Friend's email"
                schema={makeReqEmailSchema()}
                style={{ marginBottom: isMobile ? 10 : 0 }}
              />
              {values.referrals.length > 1 && (
                <div className="flex items-center h-[44px]">
                  <Icon
                    icon={faTrashCan}
                    backgroundClassName="mr-0"
                    onClick={() => handleRemove(index)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </FormikProvider>
      <div className="flex justify-between items-center">
        <Button
          variant="link"
          onClick={() => {
            setFieldValue('referrals', [
              ...values.referrals,
              { firstName: '', lastName: '', email: '' },
            ]).then(() => {
              validateForm()
            })
          }}
        >
          + Add another
        </Button>
        <Button
          disabled={!isValid || loading}
          loading={loading}
          onClick={submitForm}
        >
          Send email
        </Button>
      </div>
      {error && (
        <Banner
          variant={BannerVariants.DANGER}
          text="There was an error, please try again."
        />
      )}
      {!error &&
        !loading &&
        postResponse &&
        postResponse.successful.length > 0 && (
          <Banner
            variant={BannerVariants.SUCCESS}
            title="Success"
            text={`${postResponse.successful.map((s) => s.email).join(', ')} ${
              postResponse.successful.length > 1 ? 'have' : 'has'
            } been invited!`}
          />
        )}
      {!error &&
        !loading &&
        postResponse &&
        postResponse.erroneous.length > 0 && (
          <Banner
            variant={BannerVariants.WARNING}
            title="Hmm could you please review"
            text={createErroneousMessage(postResponse.erroneous)}
          />
        )}
    </div>
  )
}

export default ReferralEmailForm
